<template>
  <div class="resource-page financial-resource-page">
    <section class="resource-toolbar">
      <div>
        <h2>Extratos</h2>
        <p>Importe OFX ou CSV/XLSX. O sistema identifica o layout automaticamente.</p>
      </div>
      <div class="toolbar-actions">
        <v-btn icon :loading="loading" title="Atualizar" @click="load">
          <v-icon size="21">mdi-refresh</v-icon>
        </v-btn>
      </div>
    </section>

    <section class="finance-import-bar">
      <v-file-input
        v-model="importForm.file"
        label="Arquivo OFX/CSV/XLSX"
        accept=".ofx,.csv,.xlsx,.xls"
        dense
        flat
        solo
        show-size
        hide-details
        prepend-icon=""
        prepend-inner-icon="mdi-paperclip"
        class="import-file-field"
      />
      <v-select
        v-model="importForm.financial_account_id"
        :items="accountOptions"
        label="Conta financeira (opcional)"
        placeholder="Importar sem vincular conta"
        no-data-text="Nenhuma conta cadastrada em Financeiro > Contas"
        dense
        flat
        solo
        clearable
        hide-details
        persistent-placeholder
        class="import-account-field"
      />
      <v-btn-toggle v-model="activeStatementTab" mandatory dense class="statement-tabs">
        <v-btn value="imports" small>
          <v-icon left size="16">mdi-file-upload-outline</v-icon>
          Importações
        </v-btn>
        <v-btn value="lines" small>
          <v-icon left size="16">mdi-format-list-checks</v-icon>
          Linhas pendentes
        </v-btn>
      </v-btn-toggle>
      <div class="import-actions">
        <v-btn depressed color="primary" :loading="importing" :disabled="!importForm.file" @click="importStatement">
          <v-icon left size="18">mdi-file-upload-outline</v-icon>
          Importar extrato
        </v-btn>
      </div>
    </section>

    <section class="table-panel statements-panel">
      <div v-show="activeStatementTab === 'imports'">
        <BoaruaDataTable
          :headers="importHeaders"
          :items="imports"
          :loading="loading"
          item-key="id"
          :items-per-page="10"
        >
          <template v-slot:item.created_at="{ item }">
            {{ date(item.created_at) }}
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small color="offWhite" text-color="primary">{{ statusLabel(item.status) }}</v-chip>
          </template>
        </BoaruaDataTable>
      </div>

      <div v-show="activeStatementTab === 'lines'">
        <div class="bulk-actions-bar" :class="{ active: selectedLineCount }">
          <span>{{ selectedLineCount ? `${selectedLineCount} linha(s) selecionada(s)` : "Selecione linhas pendentes para aplicar ações em massa" }}</span>
          <div>
            <v-btn small text color="error" :disabled="!selectedLineCount || bulkProcessing" title="Rejeitar linhas selecionadas" @click="openBulkDialog('reject')">
              <v-icon left size="16">mdi-close-circle-outline</v-icon>
              Rejeitar
            </v-btn>
            <v-btn small text color="primary" :disabled="!selectedLineCount || bulkProcessing" title="Aprovar linhas selecionadas" @click="openBulkDialog('approve')">
              <v-icon left size="16">mdi-check-circle-outline</v-icon>
              Aprovar
            </v-btn>
          </div>
        </div>
        <BoaruaDataTable
          v-model="selectedLines"
          :headers="lineHeaders"
          :items="lines"
          :loading="loading"
          item-key="id"
          show-select
          :items-per-page="25"
          :footer-props="lineFooterProps"
        >
          <template v-slot:item.amount="{ item }">
            <strong :class="item.direction === 'payable' ? 'negative' : 'positive'">{{ money(item.amount) }}</strong>
          </template>
          <template v-slot:item.transaction_date="{ item }">
            {{ date(item.transaction_date) }}
          </template>
          <template v-slot:item.direction="{ item }">
            {{ directionLabel(item.direction) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small title="Conciliar" @click="openReconcile(item)">
              <v-icon size="19">mdi-check-decagram-outline</v-icon>
            </v-btn>
          </template>
        </BoaruaDataTable>
      </div>
    </section>

    <v-dialog v-model="bulkDialog" max-width="480" persistent>
      <v-card class="form-dialog bulk-dialog">
        <v-card-title>
          <div>
            <strong>{{ bulkDialogTitle }}</strong>
            <span>{{ bulkDialogMessage }}</span>
          </div>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="bulkProcessing" @click="closeBulkDialog">Cancelar</v-btn>
          <v-btn
            depressed
            :color="bulkAction === 'reject' ? 'error' : 'primary'"
            :loading="bulkProcessing"
            @click="confirmBulkAction"
          >
            {{ bulkAction === "reject" ? "Rejeitar" : "Aprovar" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="reconcileDialog" max-width="520">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>Conciliar lançamento</strong>
            <span>{{ selectedLine && selectedLine.description }}</span>
          </div>
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="reconcileForm.financial_account_id"
            :items="accountOptions"
            label="Conta financeira (opcional)"
            no-data-text="Nenhuma conta cadastrada em Financeiro > Contas"
            outlined
            dense
            clearable
            hide-details="auto"
          />
          <v-select
            v-model="reconcileForm.payment_method"
            :items="paymentMethodOptions"
            label="Forma de pagamento"
            outlined
            dense
            hide-details="auto"
            class="mt-3"
          />
          <v-select
            v-model="reconcileForm.monthly_charge_reference_month"
            :items="monthlyChargeReferenceOptions"
            label="Mês do pagamento da residência (opcional)"
            placeholder="Use apenas quando a entrada pagar mensalidade"
            no-data-text="Nenhuma mensalidade pendente encontrada"
            :disabled="selectedLine && selectedLine.direction === 'payable'"
            outlined
            dense
            clearable
            hide-details="auto"
            class="mt-3"
            @change="reconcileForm.monthly_charge_id = null"
          />
          <v-autocomplete
            v-model="reconcileForm.monthly_charge_id"
            :items="filteredMonthlyChargeOptions"
            label="Cobrança da residência (opcional)"
            placeholder="Sem cobrança vinculada: tratar como Pix, receita avulsa ou doação"
            no-data-text="Nenhuma mensalidade pendente encontrada"
            :disabled="selectedLine && selectedLine.direction === 'payable'"
            outlined
            dense
            clearable
            hide-details="auto"
            class="mt-3"
            @change="handleMonthlyChargeChange"
          />
          <v-select
            v-if="reconcileForm.monthly_charge_id"
            v-model="reconcileForm.monthly_charge_settlement_scope"
            :items="monthlyChargeSettlementOptions"
            label="Considerar como pago"
            outlined
            dense
            hide-details="auto"
            class="mt-3"
          />
          <v-autocomplete
            v-if="!reconcileForm.monthly_charge_id"
            v-model="reconcileForm.member_unit_id"
            :items="memberUnitOptions"
            label="Residência vinculada (opcional)"
            placeholder="Use para doação ou contribuição extra"
            no-data-text="Nenhuma residência encontrada"
            :disabled="selectedLine && selectedLine.direction === 'payable'"
            outlined
            dense
            clearable
            hide-details="auto"
            class="mt-3"
          />
          <v-select
            v-if="reconcileForm.member_unit_id"
            v-model="reconcileForm.residence_revenue_type"
            :items="residenceRevenueTypeOptions"
            label="Tipo de recebimento da residência"
            outlined
            dense
            hide-details="auto"
            class="mt-3"
          />
          <v-textarea v-model="reconcileForm.description" label="Descrição da movimentação" outlined rows="3" hide-details="auto" class="mt-3" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reconcileDialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="reconciling" @click="reconcile">
            <v-icon left size="18">mdi-check</v-icon>
            Conciliar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";

export default {
  name: "FinancialStatements",
  components: { BoaruaDataTable },
  data() {
    return {
      loading: false,
      importing: false,
      reconciling: false,
      bulkProcessing: false,
      bulkDialog: false,
      bulkAction: null,
      activeStatementTab: "imports",
      imports: [],
      lines: [],
      selectedLines: [],
      accounts: [],
      monthlyCharges: [],
      memberUnits: [],
      selectedLine: null,
      reconcileDialog: false,
      reconcileForm: {
        financial_account_id: null,
        payment_method: "bank_statement",
        monthly_charge_reference_month: null,
        monthly_charge_id: null,
        monthly_charge_settlement_scope: "single",
        member_unit_id: null,
        residence_revenue_type: "donation",
        description: "",
      },
      importForm: {
        file: null,
        financial_account_id: null,
      },
      importHeaders: [
        { text: "Arquivo", value: "filename" },
        { text: "Linhas", value: "total_lines", width: 100 },
        { text: "Ignoradas", value: "ignored_lines", width: 110 },
        { text: "Status", value: "status", width: 120 },
        { text: "Criado em", value: "created_at", width: 150 },
      ],
      lineHeaders: [
        { text: "Data", value: "transaction_date", width: 120 },
        { text: "Descrição", value: "description" },
        { text: "Direção", value: "direction", width: 110 },
        { text: "Valor", value: "amount", width: 130 },
        { text: "", value: "actions", width: 80, sortable: false },
      ],
      lineFooterProps: {
        "items-per-page-options": [25, 50, 100, -1],
        "items-per-page-text": "Linhas por página:",
        "items-per-page-all-text": "Todas",
      },
      paymentMethodOptions: [
        { text: "Extrato bancário", value: "bank_statement" },
        { text: "Pix", value: "pix" },
        { text: "Transferência", value: "bank_transfer" },
        { text: "Dinheiro", value: "cash" },
        { text: "Cartão", value: "card" },
        { text: "Boleto", value: "bank_slip" },
      ],
      monthlyChargeSettlementOptions: [
        { text: "Somente a cobrança selecionada", value: "single" },
        { text: "Total em aberto do pagador até o mês selecionado", value: "payer_due_until_reference" },
      ],
      residenceRevenueTypeOptions: [
        { text: "Doação", value: "donation" },
        { text: "Contribuição extra", value: "extra_contribution" },
        { text: "Outro recebimento", value: "other" },
      ],
    };
  },
  computed: {
    accountOptions() {
      return this.accounts.map((account) => ({ text: account.name, value: account.id }));
    },
    memberUnitOptions() {
      return this.memberUnits.map((unit) => ({
        text: this.memberUnitLabel(unit),
        value: unit.id,
      }));
    },
    monthlyChargeOptions() {
      return this.monthlyCharges.map((charge) => ({
        text: this.monthlyChargeLabel(charge),
        value: charge.id,
      }));
    },
    filteredMonthlyChargeOptions() {
      const reference = this.reconcileForm.monthly_charge_reference_month;
      return this.monthlyCharges
        .filter((charge) => !reference || charge.reference_month === reference)
        .map((charge) => ({
          text: this.monthlyChargeLabel(charge),
          value: charge.id,
        }));
    },
    monthlyChargeReferenceOptions() {
      const references = [...new Set(this.monthlyCharges.map((charge) => charge.reference_month).filter(Boolean))];
      return references.sort().map((reference) => ({
        text: this.referenceMonth(reference),
        value: reference,
      }));
    },
    selectedLineIds() {
      return this.selectedLines.map((line) => line.id).filter(Boolean);
    },
    selectedLineCount() {
      return this.selectedLineIds.length;
    },
    bulkDialogTitle() {
      return this.bulkAction === "reject" ? "Rejeitar linhas pendentes" : "Aprovar linhas pendentes";
    },
    bulkDialogMessage() {
      const total = this.selectedLineCount;
      if (this.bulkAction === "reject") {
        return `As ${total} linha(s) selecionada(s) serão marcadas como ignoradas e não entrarão no fluxo de caixa.`;
      }
      return `As ${total} linha(s) selecionada(s) serão conciliadas como movimentações financeiras e aparecerão na prestação de contas.`;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [importsResponse, linesResponse, accountsResponse, pendingChargesResponse, overdueChargesResponse, memberUnitsResponse] = await Promise.all([
          axios.get("/financial/statement-imports", { params: { limit: 20 } }),
          axios.get("/financial/statement-lines", { params: { limit: 500, status: "pending" } }),
          axios.get("/financial/accounts", { params: { limit: 100, status: "active" } }),
          axios.get("/monthly-charges", { params: { limit: 200, status: "pending" } }),
          axios.get("/monthly-charges", { params: { limit: 200, status: "overdue" } }),
          axios.get("/member-units", { params: { limit: 300, status: "active" } }),
        ]);
        this.imports = this.contentRows(importsResponse);
        this.lines = this.contentRows(linesResponse);
        this.selectedLines = [];
        this.accounts = this.contentRows(accountsResponse);
        this.monthlyCharges = this.uniqueById([
          ...this.contentRows(pendingChargesResponse),
          ...this.contentRows(overdueChargesResponse),
        ]);
        this.memberUnits = this.contentRows(memberUnitsResponse);
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível carregar os extratos");
      } finally {
        this.loading = false;
      }
    },
    async importStatement() {
      if (!this.importForm.file) return;
      this.importing = true;
      try {
        const formData = new FormData();
        Object.keys(this.importForm).forEach((key) => {
          if (this.importForm[key] !== null && this.importForm[key] !== "") {
            formData.append(key, this.importForm[key]);
          }
        });
        await axios.post("/financial/statement-imports", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.importForm.file = null;
        this.notifySuccess("Extrato importado");
        await this.load();
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível importar o extrato");
      } finally {
        this.importing = false;
      }
    },
    openReconcile(item) {
      this.selectedLine = item;
      this.reconcileForm = {
        financial_account_id: item.financial_account_id || null,
        payment_method: "bank_statement",
        monthly_charge_reference_month: null,
        monthly_charge_id: null,
        monthly_charge_settlement_scope: "single",
        member_unit_id: null,
        residence_revenue_type: "donation",
        description: item.description || "",
      };
      this.reconcileDialog = true;
    },
    handleMonthlyChargeChange(value) {
      if (value) {
        this.reconcileForm.member_unit_id = null;
        this.reconcileForm.residence_revenue_type = "donation";
      }
    },
    async reconcile() {
      if (!this.selectedLine) return;
      this.reconciling = true;
      try {
        await axios.post(`/financial/statement-lines/${this.selectedLine.id}/reconcile`, this.reconcileForm);
        this.notifySuccess("Linha conciliada");
        this.reconcileDialog = false;
        await this.load();
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível conciliar a linha");
      } finally {
        this.reconciling = false;
      }
    },
    openBulkDialog(action) {
      if (!this.selectedLineCount) return;
      this.bulkAction = action;
      this.bulkDialog = true;
    },
    closeBulkDialog() {
      if (this.bulkProcessing) return;
      this.bulkDialog = false;
      this.bulkAction = null;
    },
    async confirmBulkAction() {
      if (!this.selectedLineCount || !this.bulkAction) return;
      this.bulkProcessing = true;
      try {
        const endpoint = this.bulkAction === "reject"
          ? "/financial/statement-lines/bulk-ignore"
          : "/financial/statement-lines/bulk-reconcile";
        const response = await axios.post(endpoint, {
          ids: this.selectedLineIds,
          payment_method: "bank_statement",
        });
        const result = response.data.content || {};
        const processed = Number(result.processed || 0);
        const skipped = Number(result.skipped || 0);
        const errors = Array.isArray(result.errors) ? result.errors.length : 0;
        const actionLabel = this.bulkAction === "reject" ? "rejeitadas" : "aprovadas";
        this.notifySuccess(`${processed} linha(s) ${actionLabel}${skipped ? `, ${skipped} ignorada(s)` : ""}${errors ? `, ${errors} com erro` : ""}`);
        this.bulkDialog = false;
        this.bulkAction = null;
        await this.load();
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível processar as linhas selecionadas");
      } finally {
        this.bulkProcessing = false;
      }
    },
    contentRows(response) {
      const content = response.data.content || {};
      return content.data || content || [];
    },
    uniqueById(items = []) {
      const seen = new Set();
      return items.filter((item) => {
        if (!item || !item.id || seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
    },
    monthlyChargeLabel(charge = {}) {
      const payer = charge.payer && charge.payer.user
        ? charge.payer.user.name
        : (charge.member && charge.member.user ? charge.member.user.name : "Residência");
      const reference = charge.reference_month ? this.referenceMonth(charge.reference_month) : "sem referência";
      const status = this.statusLabel(charge.status);
      return `${payer} · ${reference} · ${this.money(charge.amount)} · ${status}`;
    },
    memberUnitLabel(unit = {}) {
      const responsible = this.memberUnitResponsibleName(unit);
      const address = this.memberUnitAddress(unit);
      if (responsible && address) return `${responsible} · ${address}`;
      return responsible || address || "Residência";
    },
    memberUnitResponsibleName(unit = {}) {
      const member = unit.member || {};
      const user = unit.user || member.user || {};
      return this.capitalizePersonName(unit.responsible_name || unit.owner_name || member.name || user.name || "");
    },
    memberUnitAddress(unit = {}) {
      const street = unit.street || unit.address || unit.address_normalized || "";
      const number = unit.number || unit.house_number || "";
      if (street && number && !String(street).includes(String(number))) return `${street}, ${number}`;
      return street || number || "";
    },
    capitalizePersonName(value) {
      const particles = ["da", "das", "de", "di", "do", "dos", "du", "e"];
      return String(value || "")
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .map((part, index) => {
          if (index > 0 && particles.includes(part)) return part;
          return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join(" ");
    },
    referenceMonth(value) {
      const match = String(value || "").match(/^(\d{4})-(\d{2})$/);
      if (!match) return value || "-";
      return `${match[2]}/${match[1]}`;
    },
    directionLabel(value) {
      return value === "payable" ? "Saída" : "Entrada";
    },
    statusLabel(value) {
      const labels = {
        processed: "Processado",
        pending: "Pendente",
        reconciled: "Conciliado",
        ignored: "Ignorado",
        overdue: "Vencido",
        paid: "Pago",
        error: "Erro",
      };
      return labels[value] || value || "-";
    },
    money(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    date(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
      return date.toLocaleDateString("pt-BR");
    },
    notifySuccess(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.financial-resource-page {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.resource-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 22px;
    font-weight: 900;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.finance-import-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.import-file-field {
  min-width: 280px;
  flex: 1 1 420px;
}

.import-account-field {
  min-width: 260px;
  flex: 0 1 300px;
}

.finance-import-bar::v-deep {
  .v-input__slot {
    min-height: 44px !important;
    background: #f4f7f4 !important;
    border-radius: 999px !important;
    box-shadow: none !important;
    padding: 0 18px !important;
  }

  .v-label,
  input,
  .v-file-input__text,
  .v-select__selection {
    color: #66736b !important;
    font-size: 14px;
  }

  .v-input__prepend-inner {
    margin-right: 8px !important;
  }

  .v-input__prepend-inner .v-icon {
    color: #66736b !important;
  }
}

.import-actions {
  flex: 0 0 auto;
  display: flex;
}

.table-panel {
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
  overflow: hidden;
}

.statements-panel {
  padding-top: 0;
  padding-bottom: 0;
  overflow: hidden;
}

.panel-heading {
  margin-bottom: 12px;

  h3 {
    margin: 0;
    color: #111827;
    font-size: 17px;
    font-weight: 900;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.statements-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.bulk-actions-bar {
  min-height: 52px;
  padding: 10px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #ffffff;
  border-bottom: 1px solid #d6ddd6;

  span {
    color: #66736b;
    font-size: 13px;
    font-weight: 700;
  }

  > div {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    flex: 0 0 auto;
  }

  .v-btn {
    min-width: auto;
    padding: 0 10px !important;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
  }

  &.active span {
    color: #10223f;
  }
}

.statement-tabs {
  flex: 0 0 auto;
  overflow: hidden;
  background: #f4f7f4 !important;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
  box-shadow: none !important;

  .v-btn {
    min-width: 132px;
    height: 44px !important;
    color: #66736b;
    background: transparent !important;
    border: 0 !important;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: none;
  }

  .v-btn--active {
    color: #057a3d !important;
    background: #ffffff !important;
  }
}

.positive {
  color: #057a3d;
}

.negative {
  color: #b42318;
}

.statements-panel::v-deep {
  .v-data-table__wrapper {
    overflow-x: auto;
  }

  table {
    min-width: 760px;
  }
}

.form-dialog .v-card__title {
  div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    color: #111827;
    font-size: 18px;
  }

  span {
    color: #66736b;
    font-size: 13px;
    font-weight: 500;
  }
}

.bulk-dialog .v-card__actions {
  padding: 14px 24px 22px;
}

@media (max-width: 1280px) {
  .finance-import-bar {
    flex-wrap: wrap;
  }

  .import-file-field {
    flex: 1 1 320px;
  }

  .import-account-field {
    flex: 1 1 260px;
  }

  .statement-tabs {
    flex: 1 1 300px;
  }

  .statement-tabs .v-btn {
    flex: 1 1 0;
  }

  .bulk-actions-bar {
    flex-wrap: wrap;
  }

  .bulk-actions-bar > div {
    margin-left: auto;
  }
}

@media (max-width: 700px) {
  .resource-toolbar,
  .finance-import-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .import-file-field,
  .import-account-field,
  .statement-tabs,
  .import-actions,
  .import-actions .v-btn {
    width: 100%;
    flex: 1 1 auto;
  }

  .statement-tabs {
    display: grid;
  }

  .statement-tabs .v-btn {
    min-width: 0;
    padding: 0 8px !important;
  }
}
</style>
